import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Container, Button, Form, Dropdown, Nav, Row} from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
import {useHistory} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import Navbar from '../../../components/Navbar';
import Sidebar from '../../../components/Sidebar';
import Footer from '../../../components/Footer';
import CourseCard from './CourseCard';
import Filter from './Filter';
import {
  Main,
  CatalogList,
  CourseFilter,
  ChipText,
  Header,
  PaginationContainer,
  NavItem,
  Tab,
  Link,
  FilterContainer,
} from '../elements/catalog';
import close from '../elements/close-chip.svg';
import {useStore} from '../../../store';
import AlertComponent from '../../../components/AlertComponent';
import Icon from '../elements/bars.svg';
import FilterIcon from '../elements/filter.png';
import ScreenLoader from '../../../components/ScreenLoader';
import PageBottom from '../../../components/PageBottom';

// Course Catalog
const CourseCatalog = observer(() => {
  const {courseStore, alertStore} = useStore();
  const {
    professionsList,
    listProfessions,
    courseSpecialtiesList,
    listCourseSpecialties,
    organizationsList,
    listOrganizations,
    coursesList,
    loading,
    selectedSpecialtiesList,
    selectedProfessions,
    selectedOrganizationList,
    setSort,
    sortBy,
    sortOrder,
    courseCount,
    page,
    count,
    pageIncrement,
    pageDecrement,
    changeCount,
    listCourses,
    onRemoveSpecialty,
    onResetProfession,
    onRemoveOrganization,
    onCreateBookmark,
    onDeleteBookmark,
    playlist,
    getAllPlaylists,
    setSearch,
    searchText,
    resetSearch,
    setSelectedProfessions,
    setSelectedSpecialities,
    setSelectedOrganizations,
    setCME,
    scrollCatalog,
  } = courseStore;
  const {message, status, resetAlert} = alertStore;
  const location = useHistory();
  const [searchInputValue, setSearchInputValue] = useState(searchText);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    setSelectedOrganizations([], false);
    setSelectedProfessions([], false);
    setSelectedSpecialities([], false);
    setCME(false);
    resetSearch();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getAllPlaylists();
  }, [getAllPlaylists]);

  useEffect(() => {
    listProfessions();
    listCourseSpecialties();
    listOrganizations();
  }, [listProfessions, listCourseSpecialties, listOrganizations]);

  useEffect(() => {
    listCourses();
  }, [sortBy, sortOrder, count, searchText, listCourses]);

  const selectOptions = (selectedIDs, type) => {
    if (type === 1) {
      setSelectedProfessions(selectedIDs, false);
    } else if (type === 2) {
      setSelectedSpecialities(selectedIDs, false);
    } else {
      setSelectedOrganizations(selectedIDs, false);
    }
  };

  const clearFunc = () => {
    selectOptions([], 0);
    selectOptions([], 1);
    selectOptions([], 2);
    setCME(false);
    resetSearch();
    setSearchInputValue('');
  };

  const removeProfession = () => {
    onResetProfession();
  };

  const removeSpecialty = item => {
    onRemoveSpecialty(item.id);
  };

  const removeOrganization = item => {
    onRemoveOrganization(item.id);
  };

  // eslint-disable-next-line
  const onHandleBookmark = item => {
    const user = localStorage.getItem('user');
    if (user) {
      if (item.bookmark?.length) {
        onDeleteBookmark(item);
      } else {
        onCreateBookmark(item.id);
      }
    } else {
      location.push('/login');
    }
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const onSearchInputChange = event => {
    const value = event?.target?.value;
    setSearchInputValue(value);
    if (!value) {
      resetSearch();
    }
  };

  const submitSearch = () => {
    if (searchInputValue) {
      setSearch(searchInputValue);
    }
  };

  const onSearchInputKeyUp = event => {
    if (searchInputValue && searchInputValue.length <= 280 && event.key === 'Enter') {
      submitSearch();
    }
  };

  const [profession, setProfession] = useState(null);
  useEffect(() => {
    setProfession(professionsList.find(x => x.id === parseInt(selectedProfessions[0], 10)));
  }, [professionsList, selectedProfessions, setProfession]);

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const resizeWindow = () => {
    setWindowSize(window.innerWidth);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener('resize', resizeWindow);
    return () => window.removeEventListener('resize', resizeWindow);
  }, []);

  useEffect(() => {
    const scrollEvent = e => {
      const {scrollTop, clientHeight, scrollHeight} = e.target.scrollingElement;
      if (Math.round(scrollHeight - scrollTop) <= Math.round(clientHeight)) {
        scrollCatalog();
      }
    };
    if (windowSize <= 576) {
      window.addEventListener('scroll', e => scrollEvent(e));
      window.setTimeout(() => window.removeEventListener('scroll', e => scrollEvent(e)), 1000);
    }
  }, [scrollCatalog, windowSize]);

  return (
    <div id="courses" className="d-flex flex-column flex-fill">
      <ReactTooltip place="top" effect="solid" id="Catalog Course" />
      <Helmet
        title="GIBLIB | Catalog"
        meta={[
          {
            name: 'description',
            content:
              "Stream surgical videos and medical lectures from subject matter experts at the leading academic medical institutions on demand with unlimited access by being a subscriber to GIBLIB today. Earn CME with GIBLIB's premium plan while learning from the world's best doctors, online.",
          },
          {
            property: 'og:title',
            content: 'GIBLIB | Catalog',
          },
          {
            name: 'twitter:card',
            content: 'summary',
          },
          {
            name: 'twitter:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            name: 'image',
            property: 'og:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            property: 'og:url',
            content: window.location.origin + window.location.pathname,
          },
        ]}
      />
      <Navbar />
      <div className="d-flex">
        <Sidebar />
        <Main role="main" className="w-100">
          <Header>
            <Container>
              <h5>
                <img src={Icon} alt="icon" />
                Catalog
              </h5>
            </Container>
          </Header>
          {message ? <AlertComponent message={message} status={status} click={() => resetAlert()} /> : null}
          <Container>
            <Tab>
              <Nav>
                <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/videos">
                    Videos
                  </Link>
                </NavItem>
                {/* <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/courses">
                    Courses
                  </Link>
                </NavItem> */}
                <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/conferences">
                    Conferences
                  </Link>
                </NavItem>
              </Nav>
            </Tab>
            <Row>
              <CourseFilter className="col-md-4">
                <h5>
                  <PaginationContainer className="sort-mobile">
                    <Dropdown>
                      <Dropdown.Toggle variant="basic" id="sort" className="a_sort">
                        Sort by
                      </Dropdown.Toggle>

                      <Dropdown.Menu onChange={e => setSort(e.target.value)}>
                        <Form.Check
                          type="radio"
                          label="Name (A-Z)"
                          id="alphabet-asc"
                          value="Name (A-Z)"
                          name="sort"
                          checked={sortOrder === 'ASC' && sortBy === 'title'}
                        />
                        <Form.Check
                          type="radio"
                          label="Name (Z-A)"
                          id="alphabet-desc"
                          value="Name (Z-A)"
                          name="sort"
                          checked={sortOrder === 'DESC' && sortBy === 'title'}
                        />
                        <Form.Check
                          type="radio"
                          label="Date Added (Newest)"
                          id="newest"
                          value="Date Added (Newest)"
                          name="sort"
                          checked={sortOrder === 'DESC' && sortBy === 'created_at'}
                        />
                        <Form.Check
                          type="radio"
                          label="Date Added (Oldest)"
                          id="oldest"
                          value="Date Added (Oldest)"
                          name="sort"
                          checked={sortOrder === 'ASC' && sortBy === 'created_at'}
                        />
                      </Dropdown.Menu>
                    </Dropdown>
                  </PaginationContainer>
                  <Button className={`filter-btn ${!showFilter ? 'show' : 'hide'}`} onClick={() => setShowFilter(true)}>
                    <img src={FilterIcon} alt="" />
                  </Button>
                  <div className="filter-img">
                    <img src={FilterIcon} alt="" />
                    Filter
                  </div>
                </h5>
                <FilterContainer className={showFilter ? 'show' : 'hide'}>
                  <Form.Row className="justify-content-between m-0 px-3 hide-mobile">
                    <span>
                      {courseCount === 1 ? 'Result' : 'Results'} {courseCount}
                    </span>
                    <Button onClick={clearFunc} className="clear-btn" type="reset">
                      Reset
                    </Button>
                  </Form.Row>
                  <Form.Row className="justify-content-between m-0 px-3 show-mobile">
                    <h5>
                      <Button className="filter-btn" onClick={() => setShowFilter(false)}>
                        <img src={FilterIcon} alt="" />
                      </Button>
                      Filter
                    </h5>
                    <div>
                      <Button onClick={clearFunc} className="clear-btn" type="reset">
                        Reset
                      </Button>
                      <Button onClick={() => setShowFilter(false)} className="closeFilter">
                        <img alt="close" src={close} />
                      </Button>
                    </div>
                  </Form.Row>
                  <hr />
                  <div className="d-flex container flex-wrap mb-3">
                    {profession ? (
                      <ChipText>
                        {profession.name}
                        <span>
                          <button type="button" className="bg-transparent border-0" onClick={() => removeProfession()}>
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ) : null}
                    {selectedSpecialtiesList.map(item => (
                      <ChipText>
                        {item.name}
                        <span>
                          <button
                            type="button"
                            className="bg-transparent border-0"
                            onClick={() => removeSpecialty(item)}
                          >
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ))}
                    {selectedOrganizationList.map(item => (
                      <ChipText>
                        {item.name}
                        <span>
                          <button
                            type="button"
                            className="bg-transparent border-0"
                            onClick={() => removeOrganization(item)}
                          >
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ))}
                  </div>
                  <Form.Row className="input-group form-inline m-0">
                    <div className="input-group-prepend">
                      <button className="btn btn-outline-secondary search-button" type="submit" onClick={submitSearch}>
                        <i className="far fa-search" />
                      </button>
                    </div>
                    <input
                      id="search-catalog"
                      value={searchInputValue}
                      className="form-control search control-bottom"
                      type="search"
                      placeholder="Search Catalog"
                      aria-label="search"
                      name="search"
                      onChange={onSearchInputChange}
                      onKeyUp={onSearchInputKeyUp}
                      autoComplete="off"
                    />
                  </Form.Row>
                  <Filter
                    professionFilter={professionsList}
                    specialityFilter={courseSpecialtiesList}
                    organizationFilter={organizationsList}
                  />
                </FilterContainer>
              </CourseFilter>
              <CatalogList className="col-sm-8">
                <Container id="course_container" className="pr-0">
                  <div className="pb-3 d-flex justify-content-end">
                    <PaginationContainer>
                      <Dropdown>
                        <Dropdown.Toggle variant="basic" id="sort" className="a_sort">
                          Sort by
                        </Dropdown.Toggle>

                        <Dropdown.Menu onChange={e => setSort(e.target.value)}>
                          <Form.Check
                            type="radio"
                            label="Name (A-Z)"
                            id="alphabet-asc"
                            value="Name (A-Z)"
                            name="sort"
                            checked={sortOrder === 'ASC' && sortBy === 'title'}
                          />
                          <Form.Check
                            type="radio"
                            label="Name (Z-A)"
                            id="alphabet-desc"
                            value="Name (Z-A)"
                            name="sort"
                            checked={sortOrder === 'DESC' && sortBy === 'title'}
                          />
                          <Form.Check
                            type="radio"
                            label="Date Added (Newest)"
                            id="newest"
                            value="Date Added (Newest)"
                            name="sort"
                            checked={sortOrder === 'DESC' && sortBy === 'created_at'}
                          />
                          <Form.Check
                            type="radio"
                            label="Date Added (Oldest)"
                            id="oldest"
                            value="Date Added (Oldest)"
                            name="sort"
                            checked={sortOrder === 'ASC' && sortBy === 'created_at'}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </PaginationContainer>
                    <PaginationContainer className="text-right">
                      <span>
                        {courseCount === 0 ? <>0 </> : <>{page * count - (count - 1)} </>}-{' '}
                        {page * count > courseCount ? <>{courseCount} </> : <>{page * count} </>}
                        of {courseCount}
                      </span>
                      <Button
                        onClick={pageDecrement}
                        variant="basic"
                        className="a_p_dec"
                        disabled={page * count - (count - 1) === 1}
                      >
                        <i className="far fa-angle-left" />
                      </Button>
                      <Button
                        onClick={pageIncrement}
                        variant="basic"
                        className="a_p_inc"
                        disabled={page * count >= courseCount}
                      >
                        <i className="far fa-angle-right" />
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle variant="basic" id="limitMenu" className="a_limit">
                          <i className="fas fa-ellipsis-v" />
                        </Dropdown.Toggle>

                        <Dropdown.Menu onChange={e => changeCount(e.target.value)}>
                          <Form.Check
                            type="radio"
                            label="10 per page"
                            id="limit_10"
                            value="10"
                            name="count"
                            checked={parseInt(count, 10) === 10}
                          />
                          <Form.Check
                            type="radio"
                            label="25 per page"
                            id="limit_25"
                            value="25"
                            name="count"
                            checked={parseInt(count, 10) === 25}
                          />
                          <Form.Check
                            type="radio"
                            label="50 per page"
                            id="limit_50"
                            value="50"
                            name="count"
                            checked={parseInt(count, 10) === 50}
                          />
                          <Form.Check
                            type="radio"
                            label="100 per page"
                            id="limit_100"
                            value="100"
                            name="count"
                            checked={parseInt(count, 10) === 100}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </PaginationContainer>
                  </div>
                  {!loading || window.innerWidth < 576 ? (
                    coursesList.map(course => (
                      <CourseCard
                        key={course.id}
                        item={course}
                        type="course"
                        playlist={playlist}
                        onBookmark={onHandleBookmark}
                      />
                    ))
                  ) : (
                    <ScreenLoader containerHeight="calc(50vh)" />
                  )}
                  {loading && window.innerWidth < 576 && <ScreenLoader containerHeight="calc(50px)" />}
                </Container>
              </CatalogList>
            </Row>
          </Container>
          {!loading && <Footer />}
        </Main>
      </div>
      <PageBottom />
    </div>
  );
});

export default CourseCatalog;
